import React, { useEffect, useState, useRef } from 'react';
// @ts-ignore
import { useSearchParams } from 'react-router-dom';
import { API_BASE } from '../../lib/env';
import { useAuth } from '../../hooks/useAuth.tsx';
import RadioInterface from './RadioInterface';

const RadioPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const didRequest = useRef(false);

  useEffect(() => {
    if (didRequest.current) return;
    didRequest.current = true;

    const initializeToken = async () => {
      // Get token from URL parameters
      const urlToken = searchParams.get('token');
      const role = searchParams.get('role');
      
      if (urlToken) {
        console.log('[RadioPage] Using token from URL:', urlToken, 'role:', role);
        setToken(urlToken);
        setIsLoading(false);
        return;
      }

      // If no token in URL, try to get it from Electron API
      if ((window as any).radioAPI) {
        const urlParams = (window as any).radioAPI.getUrlParams();
        if (urlParams.token) {
          setToken(urlParams.token);
          setIsLoading(false);
          return;
        }
      }

      // If running in browser without token, try to get one from server
      try {
        // Check if user is authenticated
        if (!user) {
          console.error('[RadioPage] User not authenticated');
          setIsLoading(false);
          return;
        }

        // Use the actual logged-in user's username and role from URL
        const userId = user.username;
        const userRole = role || 'unit'; // Get role from URL param
        console.log('[RadioPage] Requesting token from server for user:', userId, 'role:', userRole);
        
        // Get the auth token from localStorage to send with request
        const authToken = localStorage.getItem('cad_token');
        
        const response = await fetch(`${API_BASE}/radio/auth/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authToken ? `Bearer ${authToken}` : '',
          },
          body: JSON.stringify({
            userId: userId,
            role: userRole
          })
        });

        if (response.ok) {
          const data = await response.json();
          console.log('[RadioPage] Received token from server:', data.token);
          setToken(data.token);
        } else {
          console.error('Failed to get radio token:', response.status);
        }
      } catch (error) {
        console.error('Error requesting radio token:', error);
      }

      setIsLoading(false);
    };

    initializeToken();
  }, [user, searchParams]); // Re-run when user is loaded

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#101216',
        color: '#eceef1'
      }}>
        <div>Loading radio interface...</div>
      </div>
    );
  }

  if (!token) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#101216',
        color: '#eceef1',
        gap: '20px',
        padding: '40px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '64px', marginBottom: '10px' }}>🔒</div>
        <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#ff6e6e' }}>
          No Radio Token
        </div>
        <div style={{ fontSize: '16px', color: '#8c9198', maxWidth: '500px' }}>
          Unable to access radio system without authentication token. 
          Please launch the radio interface from the main CAD application.
        </div>
        <button 
          style={{
            background: '#3c8cff',
            border: 'none',
            borderRadius: '8px',
            color: '#ffffff',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}
          onClick={() => window.close()}
        >
          Close Window
        </button>
      </div>
    );
  }

  return <RadioInterface token={token} />;
};

export default RadioPage;
